import {decodeJwt} from "jose";
import React, {
    createContext,
    lazy,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import type {USER_TYPE} from "@/types/UserType.ts";
import {rememberSignOutReason} from "@/utils/signOutReason.ts";
import SignIn from "../../pages/SignIn";

const InstructorSignIn = lazy(() => import("@/pages/instructor/InstructorSignIn.tsx"));

type Props = {
    children?: ReactNode;
};

export type User = {
    accessToken: string;
    apiFetch: ApiFetch;
    expires: number;
    id: string;
};

export type FinancialAid = "none" | "free" | "reduced";

export type JWTUser = User & {
    nameFirst: string;
    nameLast: string;
    financialAid: FinancialAid;
    financialAidRegistration?: FinancialAid;
    activityPass: boolean;
    userType: USER_TYPE;
    studentId: string;
};

export type PartialJWTUser = Omit<JWTUser, "expires" | "apiFetch">;

export const isJWTUser = (user: JWTUser | JWTInstructor | null): user is JWTUser => {
    return user !== null && "userType" in user;
};
export const isJWTInstructor = (user: JWTUser | JWTInstructor | null): user is JWTUser => {
    return user !== null && "employeeID" in user;
};

export const ROLE_INSTRUCTOR = "instructor";
export const ROLE_EMPLOYEE = "employee";
export const ROLE_FEE_ADMIN = "fee_admin";

export type JWTInstructor = User & {
    username: string;
    employeeID: string;
    displayName: string;
    role: (typeof ROLE_INSTRUCTOR | typeof ROLE_EMPLOYEE | typeof ROLE_FEE_ADMIN)[];
};

export const INSTRUCTOR_TOKEN = "JWTInstructor";
export const USER_TOKEN = "JWTUser";

const SIGN_IN_TIMEOUT_MS = 30_000;
const SIGN_OUT_TIMEOUT_MS = 5_000;

export const jwtContext = createContext<JWTUser | null>(null);
export const jwtInstructorContext = createContext<{
    user: JWTInstructor | null;
    setUser: (user: JWTInstructor | null) => void;
}>({
    user: null,
    setUser: (_user: JWTInstructor | null) => {
        alert("there was a application error.");
    },
});

// This probably could / should be made into something more generic, but it's just tightly
// coupled enough right now to use this in-between design for now
const JWTProvider: React.FC<Props> = ({children}: Props) => {
    const [user, setUser] = useState<JWTUser | null>(null);

    const setUserFromString = (user: string) => {
        try {
            const localUser: JWTUser = JSON.parse(user);
            if (localUser && localUser.accessToken) {
                localUser.apiFetch = authenticatedApiFetchGeneric(localUser.accessToken);
                setUser(localUser);
            } else {
                setUser(null);
            }
        } catch (e) {
            setUser(null);
        }
    };

    useEffect(() => {
        window.fmSetUser = (user: string) => {
            localStorage.setItem(USER_TOKEN, user);
            setUserFromString(user);
        };

        if (window.location.hash && window.location.hash.indexOf("studentId")) {
            localStorage.removeItem(USER_TOKEN);
            return; //doing auto login magic stand back.
        }

        const user = localStorage.getItem(USER_TOKEN);
        if (user) {
            setUserFromString(user);
        }
    }, []);

    useEffect(() => {
        if (!user) {
            return;
        }
        const triggerTimeout = (user.expires - Date.now() / 1000 - 59) * 1000;
        if (triggerTimeout < 0) {
            return;
        }

        const timer = setTimeout(async () => {
            if (!user?.apiFetch) {
                return;
            }
            await refreshToken(user, setUser, USER_TOKEN);
        }, triggerTimeout);
        return () => {
            timer !== null && clearTimeout(timer);
        };
    }, [user]);

    if (!user || user.expires - Date.now() / 1000 - 60 < 0) {
        return <SignIn setUser={setUser} />;
    }

    return <jwtContext.Provider value={user}>{children}</jwtContext.Provider>;
};

async function refreshToken<T extends User>(
    user: T,
    setUser: (user: T | null) => void,
    storageKey: typeof INSTRUCTOR_TOKEN | typeof USER_TOKEN,
) {
    const url = new URL("/v1/identity/refresh-token", apiEndpoint);
    try {
        const refresh = await user.apiFetch(url.toString(), {method: "POST"});

        if (!refresh.ok) {
            console.error("error refreshing token");
            setUser(null); //send the user to the login screen
        }

        const newToken = await refresh.json();

        if (!newToken.accessToken) {
            console.error("error refreshing token", newToken);
            setUser(null); //send the user to the login screen
        }

        const claims = decodeJwt(newToken.accessToken);
        const newUser = {
            ...user,
            accessToken: newToken.accessToken,
            apiFetch: authenticatedApiFetchGeneric(newToken.accessToken),
            expires: claims.exp,
        } as T;
        localStorage.setItem(storageKey, JSON.stringify(newUser));

        setUser(newUser);
    } catch (e) {
        console.error("error refreshing token", e);
    }
}

export const JWTInstructorProvider: React.FC<Props> = ({children}: Props) => {
    const [user, setUser] = useState<JWTInstructor | null>(null);

    const setInstructor = (user: string) => {
        try {
            const localUser: JWTInstructor = JSON.parse(user);
            if (localUser?.accessToken) {
                localUser.apiFetch = authenticatedApiFetchGeneric(localUser.accessToken);
                const jwt = decodeJwt(localUser.accessToken) as {
                    sub: string;
                    role: [];
                    iat: number;
                    exp: number;
                };
                localUser.role = Array.isArray(jwt.role) ? jwt.role : [];
                setUser(localUser);
            } else {
                localStorage.removeItem(INSTRUCTOR_TOKEN);
                setUser(null);
            }
        } catch (e) {
            localStorage.removeItem(INSTRUCTOR_TOKEN);
            setUser(null);
        }
    };

    useEffect(() => {
        window.fmSetInstructor = (user: string) => {
            localStorage.setItem(INSTRUCTOR_TOKEN, user);
            setInstructor(user);
        };
        const localItem = localStorage.getItem(INSTRUCTOR_TOKEN);
        if (localItem) {
            setInstructor(localItem);
        }
    }, []);

    useEffect(() => {
        if (!user) {
            return;
        }
        const triggerTimeout = (user.expires - Date.now() / 1000 - 59) * 1000;
        if (triggerTimeout < 0) {
            return;
        }

        const timer = setTimeout(async () => {
            if (!user?.apiFetch) {
                return;
            }
            await refreshToken(user, setUser, INSTRUCTOR_TOKEN);
        }, triggerTimeout);
        return () => {
            timer !== null && clearTimeout(timer);
        };
    }, [user]);

    // if their token is only good for a minute lets send them to the login screen as it could have already expired due to clock drift
    if (!user || user.expires - Date.now() / 1000 - 60 < 0) {
        return (
            <jwtInstructorContext.Provider
                value={{
                    user,
                    setUser,
                }}
            >
                <InstructorSignIn />
            </jwtInstructorContext.Provider>
        );
    }

    return (
        <jwtInstructorContext.Provider
            value={{
                user,
                setUser,
            }}
        >
            {children}
        </jwtInstructorContext.Provider>
    );
};

export const apiEndpoint = import.meta.env.VITE_APP_API_ENDPOINT;
export type ApiFetch = (url: string | URL, init?: RequestInit) => Promise<Response>;

type AuthenticateUser = (studentId: string, birthDate: string) => Promise<JWTUser | null>;
type AuthenticateInstructor = (username: string, password: string) => Promise<JWTInstructor | null>;

export const useAuthenticateUser = (): AuthenticateUser => {
    return useCallback(async (studentId: string, dob: string): Promise<JWTUser | null> => {
        const init: RequestInit = {
            headers: new Headers({"Content-Type": "application/json"}),
            method: "POST",
        };
        const url = new URL("/v1/student", apiEndpoint);
        init.body = JSON.stringify({
            studentId: studentId,
            dob: dob,
        });
        init.signal = AbortSignal.timeout(SIGN_IN_TIMEOUT_MS);
        const response = await fetch(url.toString(), init);

        if (response.status !== 200) {
            return null;
        }

        const user = await response.json();
        return persistUser(user);
    }, []);
};

export const persistUser = (user: PartialJWTUser): JWTUser => {
    const claims = decodeJwt(user.accessToken);
    const expires = claims.exp ?? 0;
    localStorage.setItem(
        USER_TOKEN,
        JSON.stringify({
            ...user,
            expires,
        }),
    );
    return {
        ...user,
        expires,
        apiFetch: authenticatedApiFetchGeneric(user.accessToken),
    };
};

export const useAuthenticateInstructor = (): AuthenticateInstructor => {
    return useCallback(
        async (username: string, password: string): Promise<JWTInstructor | null> => {
            const init: RequestInit = {
                headers: new Headers({"Content-Type": "application/json"}),
                method: "POST",
            };
            const url = new URL("/v1/instructor", apiEndpoint);
            init.body = JSON.stringify({
                username: username,
                password: password,
            });
            init.signal = AbortSignal.timeout(SIGN_IN_TIMEOUT_MS);
            const response = await fetch(url.toString(), init);
            if (response.status !== 200) {
                return null;
            }

            const user = await response.json();
            const claims = decodeJwt(user.accessToken);
            user.expires = claims.exp;
            user.role = claims.role;
            localStorage.setItem(INSTRUCTOR_TOKEN, JSON.stringify(user));
            user.apiFetch = authenticatedApiFetchGeneric(user.accessToken);

            return user;
        },
        [],
    );
};

const signOutGeneric = (key: string) => {
    return localStorage.removeItem(key);
};

export const removeStudentLocalStorage = () => signOutGeneric(USER_TOKEN);
export const removeInstructorLocalStorage = () => signOutGeneric(INSTRUCTOR_TOKEN);

export const endServerSession = async (accessToken: string | undefined): Promise<void> => {
    if (!accessToken) {
        return;
    }

    try {
        await fetch(new URL("/v1/identity/sign-out", apiEndpoint), {
            method: "POST",
            headers: {Authorization: `Bearer ${accessToken}`},
            keepalive: true,
            signal: AbortSignal.timeout(SIGN_OUT_TIMEOUT_MS),
        });
    } catch (e) {
        console.error("error ending the session on the server", e);
    }
};

export const useSignOutInstructor = () => {
    const {setUser} = useContext(jwtInstructorContext);
    return () => {
        signOutGeneric(INSTRUCTOR_TOKEN);
        if (setUser) {
            setUser(null);
        }
    };
};

export const authenticatedApiFetchGeneric = (token: string): ApiFetch => {
    return async (url: string | URL, init?: RequestInit): Promise<Response> => {
        const requestInit = init ?? {};

        requestInit.headers =
            requestInit.headers instanceof Headers
                ? requestInit.headers
                : new Headers(requestInit.headers);
        requestInit.headers.set("Authorization", `Bearer ${token}`);
        requestInit.headers.set("Content-Type", "application/json");

        const response = await fetch(url, requestInit);

        if (response.status === 401) {
            await rememberSignOutReason(response);
            removeStudentLocalStorage();
            removeInstructorLocalStorage();
            window.location.href = "/";

            // the page is leaving, so callers must never see the 401 body as if it were their data
            return new Promise<Response>(() => undefined);
        }

        return response;
    };
};

type UseUserReturn =
    | {
          user: JWTUser;
          isStudentOrCM: true;
          isEmployee: false;
          isGuestUser: boolean;
      }
    | {
          user: JWTInstructor;
          isStudentOrCM: false;
          isEmployee: true;
          isGuestUser: false;
      }
    | {
          user: null;
          isStudentOrCM: false;
          isEmployee: false;
          isGuestUser: false;
      };

export const useUser = (): UseUserReturn => {
    const user = useContext(jwtContext);
    const {user: instructor} = useContext(jwtInstructorContext);

    return useMemo(() => {
        if (user) {
            return {
                user,
                isStudentOrCM: true,
                isEmployee: false,
                isGuestUser: user.userType === "guest_user",
            };
        }
        if (instructor) {
            return {
                user: instructor,
                isStudentOrCM: false,
                isEmployee: true,
                isGuestUser: false,
            };
        }
        return {
            user: null,
            isStudentOrCM: false,
            isEmployee: false,
            isGuestUser: false,
        };
    }, [user, instructor]);
};

export default JWTProvider;
